import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, Output, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgxMatInputTelComponent } from 'ngx-mat-input-tel';
import { ModalComponent } from '../../../ui/modal-component/modal-component.component';
import { InputFieldComponent } from '../../../ui/inputField/inputField.component';
import { CustomizableButtonComponent } from '../../../ui/customizable-button/customizable-button.component';
import { IContactFormDataKeys } from '../../create-contact-form/create-contact-form.component';
import { SettingsService } from '../../../../services/settings/settings.service';
import { ITestContact } from '../../../../types/settingsDataModule';
import { countries } from '../../../../../utils/countriesList';
import { countriesOperator } from '../../../../../utils/utils';

type FeedbackType = 'success' | 'error' | 'info' | 'warning';

/** Name and number are all a test send needs; the email is optional. */
@Component({
  selector: 'app-add-test-user-form',
  standalone: true,
  imports: [
    CommonModule,
    ModalComponent,
    InputFieldComponent,
    ReactiveFormsModule,
    NgxMatInputTelComponent,
    CustomizableButtonComponent,
  ],
  templateUrl: './add-test-user-form.component.html',
  styleUrls: ['../../../../pages/organization/settings/settings-ui.css'],
})
export class AddTestUserFormComponent implements OnDestroy {
  constructor(private builder: FormBuilder) {}
  @Output() onClose = new EventEmitter();
  @Output() fetchTestContacts = new EventEmitter();
  @Output() onSuccess = new EventEmitter<void>();
  @Input({ required: true }) openModal = false;

  settingsService = inject(SettingsService);

  submitted: boolean = false;
  isCreating: boolean = false;
  countries = countries;

  // A failed save stays in the modal until the next try.
  feedbackMessage: string = '';
  feedbackType: FeedbackType = 'success';
  showFeedback: boolean = false;
  private feedbackTimeoutId?: ReturnType<typeof setTimeout>;

  testContactForm = this.builder.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    emailAddress: ['', [Validators.email]],
    primaryContactNumber: ['', Validators.required],
    // Not asked for any more: a test send only needs a name and a number.
    // Country and dialling code still follow the phone's country.
    gender: [''],
    birthDate: [''],
    country: [''],
    zipCode: [''],
    city: [''],
  });

  getFormInput(name: IContactFormDataKeys) {
    return this.testContactForm.get(name);
  }

  get emailErrorMsg() {
    const email = this.getFormInput('emailAddress');
    if (email?.errors?.['email']) {
      return 'Enter a full email address, like name@company.com, or leave it empty.';
    }
    return '';
  }

  countryChanged(country: any) {
    const name = country?.iso2 ? countriesOperator.getName(String(country.iso2).toUpperCase(), 'en') : '';
    this.testContactForm.patchValue({
      zipCode: country?.dialCode ? `+${country.dialCode}` : '',
      country: name || country?.name || '',
    });
  }

  handleSubmit() {
    this.submitted = true;
    this.showFeedback = false;
    if (this.testContactForm.valid) {
      this.isCreating = true;
      const value = this.testContactForm.value;
      this.settingsService
        .createTestContact({
          ...(value as ITestContact),
          // Optional fields go out empty rather than as blank strings the API may reject.
          emailAddress: value.emailAddress || (undefined as any),
          gender: (value.gender || undefined) as any,
          birthDate: value.birthDate || (undefined as any),
        })
        .subscribe({
          next: () => {
            this.isCreating = false;
            this.fetchTestContacts.emit();
            this.onSuccess.emit();
            this.onClose.emit();
          },
          error: (error) => {
            this.isCreating = false;
            const errorMessage =
              error?.error?.message ||
              error?.error?.detail ||
              error?.message ||
              'The test number was not added. Check your connection and try again.';
            this.displayFeedback(errorMessage, 'error');
          },
          complete: () => {
            this.isCreating = false;
          },
        });
    }
  }

  displayFeedback(message: string, type: FeedbackType) {
    if (this.feedbackTimeoutId) {
      clearTimeout(this.feedbackTimeoutId);
    }
    this.feedbackMessage = message;
    this.feedbackType = type;
    this.showFeedback = true;
    this.feedbackTimeoutId = setTimeout(() => {
      this.showFeedback = false;
      this.feedbackTimeoutId = undefined;
    }, 10000);
  }

  ngOnDestroy() {
    if (this.feedbackTimeoutId) {
      clearTimeout(this.feedbackTimeoutId);
    }
  }
}
